import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

function initTranslations(instance, { locale, translations }, options) {
  const promise = instance.init({
    debug: false,
    ...options,
    lng: locale,
    ns: Object.keys(translations),
    interpolation: {
      escapeValue: false,
    },
    resources: {
      [locale]: translations,
    },
    react: {
      useSuspense: false,
    },
  });

  // TODO: remove when CONTENT-11314 is fixed
  instance.services.pluralResolver.addRule('ar', {
    numbers: [0, 1, 2, 3, 11, 100],
    plurals: (n) => Number(n !== 1),
  });
  // END TODO

  return promise;
}

const LocaleNamespaceMap = () => {
  let map = new Map();
  return {
    set(locale, value) {
      map.set(locale, value);
    },
    get(locale) {
      return map.get(locale);
    },
    forEach(fn) {
      map.forEach(fn);
    },
  };
};

const TranslationsManager = (options = {}) => {
  let resources = LocaleNamespaceMap();
  let pending = LocaleNamespaceMap();
  let loading = true;
  let firstStep = true;
  const i18NextInstance = i18next.createInstance().use(initReactI18next);
  let internalLocale;

  const changeLanguage = (locale, callback) => {
    i18NextInstance.changeLanguage(locale).then(callback);
  };

  const addResourceBundle = (locale, translations) => {
    const previousTranslations = resources.get(locale);
    if (previousTranslations !== translations) {
      Object.entries(translations).forEach(([namespace, nsTranslations]) => {
        i18NextInstance.addResourceBundle(
          locale,
          namespace,
          nsTranslations,
          true,
          true
        );
      });
      resources.set(locale, translations);
    }
  };

  const init = async (locale, translations, callback) => {
    loading = true;
    await initTranslations(
      i18NextInstance,
      {
        locale,
        translations,
      },
      options
    );
    resources.set(locale, translations);
    loading = false;
    callback();
    const pendingResources = pending.get(locale);
    if (pendingResources) {
      addResourceBundle(locale, pendingResources.translations);
      pendingResources.callback();
    }
    pending = LocaleNamespaceMap();
  };

  return {
    getI18next() {
      return i18NextInstance;
    },
    add(locale, translations, callback) {
      const localeChanged = locale !== internalLocale;
      internalLocale = locale;
      if (firstStep) {
        firstStep = false;
        return init(locale, translations, callback);
      }
      if (loading) {
        pending.set(locale, {
          translations: {
            ...pending.get(locale)?.translations,
            ...translations,
          },
          callback,
        });
        return;
      }
      addResourceBundle(locale, translations);
      if (localeChanged) {
        return changeLanguage(locale, callback);
      }
      callback();
    },
    clear() {
      resources.forEach((nsTranslations, locale) => {
        Object.keys(nsTranslations).forEach((namespace) =>
          i18NextInstance.removeResourceBundle(locale, namespace)
        );
      });
      resources = LocaleNamespaceMap();
    },
  };
};

export default TranslationsManager;
